import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { LocalizationService } from '@abp/ng.core';
import {
  getStatusDotClass,
  getStatusLocalizationKey,
  isPredefinedStateName,
  resolveStatusTone,
} from 'src/core/helpers/agent-status.helper';
import { PredefinedStateName } from 'src/core/constants/predefined-state-name.constant';
import { StateType } from 'src/core/models/state-definition/state-type.enum';
import { CxUserAvatarComponent } from 'src/app/shared/components/cx-user-avatar/cx-user-avatar.component';
import { AssignedAgentItem } from './models/assigned-agent-item.model';

/** Distance from the bottom of the list, in pixels, at which the next page is requested. */
const LOAD_MORE_THRESHOLD_PX = 40;

@Component({
  selector: 'app-assigned-agents',
  standalone: true,
  imports: [CommonModule, CxUserAvatarComponent],
  templateUrl: './assigned-agents.component.html',
  styleUrls: ['./assigned-agents.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignedAgentsComponent {
  private readonly localization = inject(LocalizationService);

  @Input() agents: AssignedAgentItem[] = [];
  @Input() loading = false;
  @Input() title = '';
  @Input() emptyMessage = '';
  @Input() totalCount: number | null = null;
  @Input() loadingMore = false;

  @Output() loadMore = new EventEmitter<void>();

  get count(): number {
    return this.agents?.length ?? 0;
  }

  get hasMore(): boolean {
    return this.totalCount !== null && this.count < this.totalCount;
  }

  onListScroll(event: Event): void {
    if (this.loadingMore || !this.hasMore) {
      return;
    }

    const el = event.target as HTMLElement;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < LOAD_MORE_THRESHOLD_PX) {
      this.loadMore.emit();
    }
  }

  get isEmpty(): boolean {
    return !this.loading && this.count === 0;
  }

  trackByUserId(_: number, agent: AssignedAgentItem): string {
    return agent.userId;
  }

  getStatusDotClass(agent: AssignedAgentItem): string {
    return getStatusDotClass(
      resolveStatusTone(
        agent.stateName ?? PredefinedStateName.Offline,
        agent.stateType ?? StateType.Offline,
      ),
    );
  }

  getStatusLabel(agent: AssignedAgentItem): string {
    const stateName = agent.stateName ?? PredefinedStateName.Offline;

    return isPredefinedStateName(stateName)
      ? this.localization.instant(getStatusLocalizationKey(stateName))
      : stateName;
  }
}
